<script setup lang="ts">
import { h, ref } from 'vue';
import { FhIcon } from '@fareharbor-com/beacon-vue';
import { location, search } from '@fareharbor-com/beacon-vue/icons';
import { Logger } from '@/common/Logger';
import SearchBar from '@/components/SearchBar/SearchBar.vue';
import { useLocationSearch } from '@/composables/useLocationSearch';
import { useSearchStore } from '@/stores/search';
import LocationSearchBar from './LocationSearchBar.vue';

const locationIcon = () => h(FhIcon, { icon: location });
const searchIcon = () => h(FhIcon, { icon: search });

const searchStore = useSearchStore();
const {
  handleSubmitLocationSearch,
  handleSubmitGoogleLocationSearch,
  handleClearLocationSearch,
} = useLocationSearch();

// NOTE: To forcefully re-render the search bars the technique explained here is used:
// https://stackoverflow.com/questions/32106155/can-you-force-vue-js-to-reload-re-render
const searchBarKey = ref(0);

function handleSubmitCompanyItemSearch(text: string) {
  if (searchStore.companyItemSearchText === text) {
    searchStore.companyItemSearchChanged = false;
    return;
  }
  Logger.debug(`[SearchBars] Search company / item: ${text}`);
  searchStore.companyItemSearchText = text;
  searchStore.companyItemSearchChanged = true;
}

function handleClearCompanyItemSearch() {
  Logger.debug('[SearchBars] Clearing company & item search');
  searchStore.companyItemSearchText = '';
  searchStore.companyItemSearchChanged = true;
}

function handleSearchTextChange() {
  searchBarKey.value += 1;
}

// handleSearch is called when locationSearchText/companyItemSearchText is updated in searchStore.
searchStore.$subscribe(handleSearchTextChange);
</script>

<template>
  <div
    class="flex flex-col sm:flex-row w-full gap-3 sm:gap-5"
    :key="searchBarKey"
  >
    <LocationSearchBar
      class="w-full"
      :initialText="searchStore.locationSearchText"
      :placeholder="'Search by location (city, state, region, etc.)'"
      :icon="locationIcon"
      @submitSearch="handleSubmitLocationSearch"
      @submitGoogleSearch="handleSubmitGoogleLocationSearch"
      @clearSearch="handleClearLocationSearch"
    />
    <SearchBar
      class="w-full"
      :initialText="searchStore.companyItemSearchText"
      :placeholder="'Search by company, shortname, item, or item ID'"
      :icon="searchIcon"
      @submitSearch="handleSubmitCompanyItemSearch"
      @clearSearch="handleClearCompanyItemSearch"
    />
  </div>
</template>
